import { supabase } from '../lib/supabase';
import { logger } from './loggingService';

export interface AtomicRequirement {
  id: string;
  project_id: string;
  requirement_id: string;
  type: 'functional' | 'non-functional' | 'constraint' | 'business' | 'technical';
  system: string;
  category: string;
  priority: 'must-have' | 'should-have' | 'nice-to-have' | 'critical';
  description: string;
  acceptance_criteria: string[];
  dependencies: string[];
  test_strategy?: string;
  created_via_prompt?: string;
  created_at: string;
  updated_at: string;
}

export interface UserStory {
  id: string;
  project_id: string;
  story_id: string;
  title: string;
  description: string;
  user_type: string;
  feature: string;
  benefit: string;
  acceptance_criteria: string[];
  story_points: number;
  priority: 'must-have' | 'should-have' | 'nice-to-have' | 'critical';
  epic_id?: string;
  created_via_prompt?: string;
  created_at: string;
  updated_at: string;
}

export interface Stakeholder {
  id: string;
  project_id: string;
  name: string;
  role: string;
  description?: string;
  goals: string[];
  pain_points: string[];
  influence_level: 'high' | 'medium' | 'low';
  created_via_prompt?: string;
  created_at: string;
  updated_at: string;
}

export interface Wireframe {
  id: string;
  project_id: string;
  name: string;
  description?: string;
  page_type?: string;
  canvas_data: any;
  preview_image_url?: string;
  created_via_prompt?: string;
  created_at: string;
  updated_at: string;
}

export interface RequirementsConversation {
  id: string;
  project_id: string;
  conversation_type: string;
  user_prompt: string;
  ai_response: string;
  generated_items: string[];
  created_at: string;
}

export interface ProjectBlueprint {
  id: string;
  project_id: string;
  blueprint_type: 'requirements' | 'architecture' | 'conventions' | 'security' | 'design_system';
  content: any;
  version: number;
  is_locked: boolean;
  created_via_prompt?: string;
  created_at: string;
  updated_at: string;
}

export class RequirementsService {
  // ============================================================================
  // Atomic Requirements
  // ============================================================================

  static async createAtomicRequirement(
    projectId: string,
    requirementData: Omit<AtomicRequirement, 'id' | 'project_id' | 'requirement_id' | 'created_at' | 'updated_at'>
  ): Promise<AtomicRequirement> {
    try {
      // Generate next requirement ID
      const { data: requirementId } = await supabase.rpc('generate_next_requirement_id', {
        project_uuid: projectId
      });

      const { data, error } = await supabase
        .from('atomic_requirements')
        .insert({
          project_id: projectId,
          requirement_id: requirementId,
          ...requirementData
        })
        .select()
        .single();

      if (error) throw error;

      logger.info('Atomic requirement created', { projectId, requirementId: data.requirement_id });
      return data;
    } catch (error) {
      logger.error('Error creating atomic requirement', { error, projectId });
      throw error;
    }
  }

  static async getAtomicRequirements(projectId: string): Promise<AtomicRequirement[]> {
    try {
      const { data, error } = await supabase
        .from('atomic_requirements')
        .select('*')
        .eq('project_id', projectId)
        .order('requirement_id');

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching atomic requirements', { error, projectId });
      throw error;
    }
  }

  static async updateAtomicRequirement(
    id: string,
    updates: Partial<Omit<AtomicRequirement, 'id' | 'project_id' | 'requirement_id' | 'created_at'>>
  ): Promise<AtomicRequirement> {
    try {
      const { data, error } = await supabase
        .from('atomic_requirements')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      logger.info('Atomic requirement updated', { id });
      return data;
    } catch (error) {
      logger.error('Error updating atomic requirement', { error, id });
      throw error;
    }
  }

  // ============================================================================
  // User Stories
  // ============================================================================

  static async createUserStory(
    projectId: string,
    storyData: Omit<UserStory, 'id' | 'project_id' | 'story_id' | 'created_at' | 'updated_at'>
  ): Promise<UserStory> {
    try {
      // Generate next story ID
      const { data: storyId } = await supabase.rpc('generate_next_story_id', {
        project_uuid: projectId
      });

      const { data, error } = await supabase
        .from('user_stories')
        .insert({
          project_id: projectId,
          story_id: storyId,
          ...storyData
        })
        .select()
        .single();

      if (error) throw error;

      logger.info('User story created', { projectId, storyId: data.story_id });
      return data;
    } catch (error) {
      logger.error('Error creating user story', { error, projectId });
      throw error;
    }
  }

  static async getUserStories(projectId: string): Promise<UserStory[]> {
    try {
      const { data, error } = await supabase
        .from('user_stories')
        .select('*')
        .eq('project_id', projectId)
        .order('story_id');

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching user stories', { error, projectId });
      throw error;
    }
  }

  // ============================================================================
  // Stakeholders
  // ============================================================================

  static async createStakeholder(
    projectId: string,
    data: Omit<Stakeholder, 'id' | 'project_id' | 'created_at' | 'updated_at'>
  ): Promise<Stakeholder> {
    try {
      const { data: result, error } = await supabase
        .from('stakeholders')
        .insert({
          project_id: projectId,
          ...data
        })
        .select()
        .single();

      if (error) throw error;

      logger.info('Stakeholder created', { projectId, name: data.name });
      return result;
    } catch (error) {
      logger.error('Error creating stakeholder', { error, projectId });
      throw error;
    }
  }

  static async getStakeholders(projectId: string): Promise<Stakeholder[]> {
    try {
      const { data, error } = await supabase
        .from('stakeholders')
        .select('*')
        .eq('project_id', projectId)
        .order('influence_level', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching stakeholders', { error, projectId });
      throw error;
    }
  }

  // ============================================================================
  // Wireframes
  // ============================================================================

  static async createWireframe(
    projectId: string,
    data: Omit<Wireframe, 'id' | 'project_id' | 'created_at' | 'updated_at'>
  ): Promise<Wireframe> {
    try {
      const { data: result, error } = await supabase
        .from('wireframes')
        .insert({
          project_id: projectId,
          ...data
        })
        .select()
        .single();

      if (error) throw error;

      logger.info('Wireframe created', { projectId, name: data.name });
      return result;
    } catch (error) {
      logger.error('Error creating wireframe', { error, projectId });
      throw error;
    }
  }

  static async getWireframes(projectId: string): Promise<Wireframe[]> {
    try {
      const { data, error } = await supabase
        .from('wireframes')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching wireframes', { error, projectId });
      throw error;
    }
  }

  static async updateWireframe(
    wireframeId: string,
    updates: Partial<Omit<Wireframe, 'id' | 'project_id' | 'created_at'>>
  ): Promise<Wireframe> {
    try {
      const { data, error } = await supabase
        .from('wireframes')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', wireframeId)
        .select()
        .single();

      if (error) throw error;

      logger.info('Wireframe updated', { wireframeId });
      return data;
    } catch (error) {
      logger.error('Error updating wireframe', { error, wireframeId });
      throw error;
    }
  }

  static async deleteWireframe(wireframeId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('wireframes')
        .delete()
        .eq('id', wireframeId);

      if (error) throw error;

      logger.info('Wireframe deleted', { wireframeId });
      return true;
    } catch (error) {
      logger.error('Error deleting wireframe', { error, wireframeId });
      throw error;
    }
  }

  // ============================================================================
  // Requirements Conversations
  // ============================================================================

  static async saveConversation(
    projectId: string,
    conversationType: string,
    userPrompt: string,
    aiResponse: string,
    generatedItems: string[] = []
  ): Promise<RequirementsConversation> {
    try {
      const { data, error } = await supabase
        .from('requirements_conversations')
        .insert({
          project_id: projectId,
          conversation_type: conversationType,
          user_prompt: userPrompt,
          ai_response: aiResponse,
          generated_items: generatedItems
        })
        .select()
        .single();

      if (error) throw error;

      logger.info('Requirements conversation saved', { projectId, conversationType });
      return data;
    } catch (error) {
      logger.error('Error saving requirements conversation', { error, projectId });
      throw error;
    }
  }

  static async getConversations(projectId: string, conversationType?: string): Promise<RequirementsConversation[]> {
    try {
      let query = supabase
        .from('requirements_conversations')
        .select('*')
        .eq('project_id', projectId);

      if (conversationType) {
        query = query.eq('conversation_type', conversationType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching requirements conversations', { error, projectId });
      throw error;
    }
  }

  // ============================================================================
  // Project Blueprints
  // ============================================================================

  static async saveBlueprint(
    projectId: string,
    blueprintType: ProjectBlueprint['blueprint_type'],
    content: any,
    isLocked: boolean = false,
    createdViaPrompt?: string
  ): Promise<ProjectBlueprint> {
    try {
      // Get current version
      const { data: existing } = await supabase
        .from('project_blueprints')
        .select('version')
        .eq('project_id', projectId)
        .eq('blueprint_type', blueprintType)
        .order('version', { ascending: false })
        .limit(1)
        .single();

      const version = (existing?.version || 0) + 1;

      const { data, error } = await supabase
        .from('project_blueprints')
        .insert({
          project_id: projectId,
          blueprint_type: blueprintType,
          content,
          version,
          is_locked: isLocked,
          created_via_prompt: createdViaPrompt
        })
        .select()
        .single();

      if (error) throw error;

      logger.info('Project blueprint saved', { projectId, blueprintType, version });
      return data;
    } catch (error) {
      logger.error('Error saving project blueprint', { error, projectId });
      throw error;
    }
  }

  static async getBlueprint(
    projectId: string,
    blueprintType: ProjectBlueprint['blueprint_type']
  ): Promise<ProjectBlueprint | null> {
    try {
      const { data, error } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('project_id', projectId)
        .eq('blueprint_type', blueprintType)
        .order('version', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      return data || null;
    } catch (error) {
      logger.error('Error fetching project blueprint', { error, projectId, blueprintType });
      throw error;
    }
  }

  // ============================================================================
  // AI Integration for Conversational Requirements
  // ============================================================================

  static async processRequirementsPrompt(
    projectId: string,
    prompt: string,
    conversationType: string
  ): Promise<{
    response: string;
    generatedItems: {
      requirements?: AtomicRequirement[];
      stories?: UserStory[];
      stakeholders?: Stakeholder[];
      wireframes?: Wireframe[];
    };
  }> {
    try {
      // Get userId from project
      const { data: project } = await supabase
        .from('projects')
        .select('user_id')
        .eq('id', projectId)
        .maybeSingle();

      if (!project?.user_id) {
        throw new Error('Project not found or missing user_id');
      }

      // Use AI service to process the requirements prompt
      const { AnthropicService } = await import('./anthropicService');
      
      const aiPrompt = `You are an expert requirements analyst. Help the user with ${conversationType} requirements.

User Prompt: "${prompt}"

Based on this prompt, provide:
1. A helpful response addressing their requirements question
2. Suggestions for atomic requirements, user stories, stakeholders, or wireframes as appropriate
3. Actionable next steps

Be specific and practical in your recommendations.`;

      const aiResponse = await AnthropicService.generateText(aiPrompt, {
        maxTokens: 2000,
        temperature: 0.7,
        userId: project.user_id,
      });

      const response = aiResponse.content || `I'll help you with ${conversationType}. Based on your prompt: "${prompt}", here are some suggestions...`;

      // Parse generated items from AI response if possible
      const generatedItems: {
        requirements?: AtomicRequirement[];
        stories?: UserStory[];
        stakeholders?: Stakeholder[];
        wireframes?: Wireframe[];
      } = {};

      // Try to extract structured data from AI response
      try {
        // Look for JSON in the response
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.requirements) generatedItems.requirements = parsed.requirements;
          if (parsed.stories) generatedItems.stories = parsed.stories;
          if (parsed.stakeholders) generatedItems.stakeholders = parsed.stakeholders;
          if (parsed.wireframes) generatedItems.wireframes = parsed.wireframes;
        }
      } catch (parseError) {
        logger.debug('Could not parse structured items from AI response', { error: parseError });
      }

      // Save the conversation
      await this.saveConversation(projectId, conversationType, prompt, response);

      return {
        response,
        generatedItems
      };
    } catch (error) {
      logger.error('Error processing requirements prompt', { error, projectId, prompt });
      throw error;
    }
  }
}
